const STORAGE_KEY = 'ss:prompt-video-uploads';

export type PromptVideoUploadPhase = 'presigning' | 'uploading' | 'saving' | 'done' | 'error';

export type ActivePromptVideoUpload = {
  clientId: string;
  promptId: number | null;
  progress: number;
  phase: PromptVideoUploadPhase;
  error?: string;
  startedAt: number;
};

function readAll(): Record<string, ActivePromptVideoUpload> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, ActivePromptVideoUpload>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, ActivePromptVideoUpload>) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

export function trackPromptVideoUpload(
  clientId: string,
  patch: Partial<ActivePromptVideoUpload> & { promptId?: number | null }
) {
  const map = readAll();
  const prev = map[clientId];
  map[clientId] = {
    clientId,
    promptId: patch.promptId ?? prev?.promptId ?? null,
    progress: patch.progress ?? prev?.progress ?? 0,
    phase: patch.phase ?? prev?.phase ?? 'presigning',
    error: patch.error ?? prev?.error,
    startedAt: patch.startedAt ?? prev?.startedAt ?? Date.now(),
  };
  writeAll(map);
}

export function clearPromptVideoUpload(clientId: string) {
  const map = readAll();
  delete map[clientId];
  writeAll(map);
}

const STALE_UPLOAD_MS = 30 * 60 * 1000;

/** Drop orphaned onboarding uploads and long-idle sessions. */
export function purgeStalePromptVideoUploads(now = Date.now()) {
  const map = readAll();
  let changed = false;

  Object.entries(map).forEach(([id, item]) => {
    const age = now - item.startedAt;
    const isActive = item.phase !== 'done' && item.phase !== 'error';
    const orphanedOnboarding = item.promptId == null;
    const idleTooLong = age > STALE_UPLOAD_MS;

    if (isActive && (orphanedOnboarding || idleTooLong)) {
      delete map[id];
      changed = true;
    }
  });

  if (changed) writeAll(map);
}

export function getActivePromptVideoUploads(): ActivePromptVideoUpload[] {
  purgeStalePromptVideoUploads();
  return Object.values(readAll()).filter((item) => item.phase !== 'done' && item.phase !== 'error');
}

export function getActiveUploadForPrompt(promptId: number): ActivePromptVideoUpload | null {
  return getActivePromptVideoUploads().find((item) => item.promptId === promptId) ?? null;
}

export function getAnyActiveUpload(): ActivePromptVideoUpload | null {
  const active = getActivePromptVideoUploads();
  return active.length > 0 ? active[0] : null;
}

export function newUploadClientId() {
  return `pv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
